import React, { useContext, useEffect, useState } from 'react';
import { Box, Card, Stack, Typography, TextField, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import { FieldForm } from '../_components';
import Modal from 'components/_layouts/Modal';
import { enqueueSnackbar } from 'notistack';
import { translate as t } from 'hooks/useTranslations';
import { useFieldBankContext } from '../context';
import useManageFields from '../_data_sources/useManageFields';
import Loading from 'views/App/Portal/Loading';
import { AppContext } from 'views/App/context';
import { useQueryClient } from 'react-query';

const EditField: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission, currentOrganizationId } = useContext(AppContext);
  const { fieldData, updateField, isLoading, isError } = useFieldBankContext();
  const { publish } = useManageFields();
  const queryClient = useQueryClient();
  const [changeDescription, setChangeDescription] = useState('');
  const [saveAction, setSaveAction] = useState<'Draft' | 'Active' | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [formData, setFormData] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState<any>(null);

  useEffect(() => {
    if (!hasPermission('manageFieldBank', currentOrganizationId || '')) {
      navigate('/admin/performance-reporting');
    }
  }, [hasPermission, currentOrganizationId, navigate]);

  const handleSave = async (formData: any, status: 'Draft' | 'Active') => {
    if (!fieldData) return;

    if (status === 'Active') {
      setFormData(formData);
      setSaveAction(status);
      setIsConfirmOpen(true);
    } else {
      await executeSave(formData, status, 'Field updated');
    }
  };

  const handleConfirm = async () => {
    if (!fieldData || !saveAction || !changeDescription.trim() || !formData) return;

    await executeSave(formData, saveAction, changeDescription.trim());

    // Clean up modal state
    setIsConfirmOpen(false);
    setChangeDescription('');
    setSaveAction(null);
    setFormData(null);
  };

  const executeSave = async (formData: any, status: 'Draft' | 'Active', changeNote: string) => {
    if (!fieldData) return;

    setIsSaving(true);
    try {
      // Step 1: Always update the field data first (keeping current status)
      const updatedFieldData = {
        ...fieldData,
        ...formData,
        // Keep current status for the update operation
        status: fieldData.status,
      };

      await updateField(updatedFieldData, changeNote);

      // Step 2: If user chose Active and field is currently Draft, publish it
      if (status === 'Active' && fieldData.status === 'Draft') {
        await new Promise<void>((resolve, reject) => {
          publish(
            fieldData.id,
            changeNote,
            (response) => {
              resolve();
            },
            (error) => {
              reject(error);
            }
          );
        });
      }

      enqueueSnackbar(t('performanceReporting.fieldBank.details.fieldUpdated'), {
        variant: 'success',
        autoHideDuration: 3000,
      });

      queryClient.invalidateQueries(['fields']);
      queryClient.invalidateQueries(['field', fieldData.id]);
      queryClient.invalidateQueries(['fieldAuditLog', fieldData.id]);

      navigate(`/admin/performance-reporting/field-bank/${fieldData.id}`);
    } catch (error: any) {
      console.error('Error updating/publishing field:', error);

      const errorMessage =
        status === 'Active' && fieldData.status === 'Draft'
          ? t('performanceReporting.fieldBank.details.publishError')
          : t('performanceReporting.fieldBank.details.fieldUpdateError');

      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setIsConfirmOpen(false);
    setChangeDescription('');
    setSaveAction(null);
    setFormData(null);
  };

  if (isLoading) return <Loading />;
  if (isError || !fieldData)
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Card sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant='h6'>{t('errors.genericTitle', 'Something went wrong')}</Typography>
            <Typography
              variant='body2'
              color='text.secondary'
            >
              {t(
                'performanceReporting.fieldBank.errors.loadField',
                'Unable to load field data. Please try again later.'
              )}
            </Typography>
          </Stack>
        </Card>
      </Box>
    );

  return (
    <TakeoverLayout
      pageTitle='Edit Field'
      backUrl='..'
    >
      <Box>
        <FieldForm
          onSave={handleSave}
          initialValues={fieldData}
          onCancel={() => navigate(`/admin/performance-reporting/field-bank/${fieldData.id}`)}
          fieldId={fieldData?.id}
          isEditing={true}
          serverErrors={serverErrors}
          setServerErrors={setServerErrors}
        />
      </Box>

      <Modal
        open={isConfirmOpen}
        onClose={handleClose}
        title={t('performanceReporting.fieldBank.modal.title')}
        size='sm'
        actions={
          <>
            <Button
              color='neutral'
              onClick={handleClose}
              disabled={isSaving}
            >
              {t('performanceReporting.fieldBank.modal.goBack')}
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={handleConfirm}
              disabled={!changeDescription.trim() || isSaving}
              loading={isSaving}
              loadingPosition='end'
            >
              {t('performanceReporting.fieldBank.modal.completeSave')}
            </Button>
          </>
        }
      >
        <Stack spacing={2}>
          <Typography
            variant='body1'
            sx={{ pt: 4 }}
          >
            {t('performanceReporting.fieldBank.modal.description')}
          </Typography>
          <TextField
            autoFocus
            margin='dense'
            id='changeDescription'
            label={t('performanceReporting.fieldBank.modal.noteLabel')}
            type='text'
            fullWidth
            variant='outlined'
            value={changeDescription}
            onChange={(e) => setChangeDescription(e.target.value)}
            error={false}
            multiline
            minRows={2}
          />
        </Stack>
      </Modal>
    </TakeoverLayout>
  );
};

export default EditField;
